
import { computed, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { formatDate, formatThousands } from '@/utils'
import {
  formatBanendFlag,
  formatBehaveType, formatDraftType, formatRequestEntrustPay, formatSignupMark, formatSplitFlag
} from '@/utils/acceptanceBillFormatUtil'
import { useI18n } from 'vue-i18n'
import {
  getAcceptanceBillTransfers,
  initiateApproval, refuseSignForAcceptanceBill, signForAcceptanceBill
} from '@/apis/accepted-bill/acceptanceReceipt'
import Container from '@/components/form/container/Container.vue'
import { ElMessage } from 'element-plus'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import dayjs from 'dayjs'
import {
  AcceptanceBillTransfersParamModel,
  ProcessingAcceptedBillsParamModel, ReceiptAcceptedBillTransferRecordModel, ReceiveAcceptanceHistoryModel
} from '@/model/accepted-bill/acceptedBillModel'
import { DraftModel, InitiateApprovalParamModel } from '@/model/accepted-bill/approvalFormModel'

export default defineComponent({
  name: 'AcceptanceReceiptDetail',
  methods: { formatThousands },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    approvalData: {
      type: Object,
      default: null,
      required: true
    }
  },
  components: {
    SimpleTable,
    Container
  },
  emits: ['refresh', 'update:modelValue'],
  setup(props, context) {
    const { t } = useI18n()
    const tableRef = ref()
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value)
    })
    const statusOptions = [
      { label: t('acceptanceStatus.pendingConfirmation'), value: 0 },
      { label: t('acceptanceStatus.underReview'), value: 1 },
      { label: t('acceptanceStatus.pendingAction'), value: 2 },
      { label: t('acceptanceStatus.operated'), value: 3 },
      { label: t('acceptanceStatus.received'), value: 98 },
      { label: t('acceptanceStatus.rejected'), value: 99 }
    ]
    const detailTableStruct = [
      { prop: 'behaveNo', label: t('Acceptance.behaveNo') },
      { prop: 'behaveType', label: t('Acceptance.behaveType'), formatter: (row: ReceiptAcceptedBillTransferRecordModel) => formatBehaveType(row.behaveType) },
      { prop: 'behaveDate', label: t('Acceptance.behaveDate'), formatter: (row: ReceiptAcceptedBillTransferRecordModel) => formatDate(row.behaveDate, 'yyyy-MM-dd') },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'requestBankName', label: t('Acceptance.requestBankName') },
      { prop: 'responseObject', label: t('Acceptance.responseObject') },
      { prop: 'responseBankName', label: t('Acceptance.responseBankName') },
      { prop: 'banendFlag', label: t('Acceptance.banendFlagTrue'), formatter: (row: ReceiptAcceptedBillTransferRecordModel) => formatBanendFlag(row.banendFlag) },
      { prop: 'signupMark', label: t('Acceptance.signupMark'), formatter: (row: ReceiptAcceptedBillTransferRecordModel) => formatSignupMark(row.signupMark) }
    ]
    const dataMap = reactive({
      tableData: Array<ReceiptAcceptedBillTransferRecordModel>(),
      columnStruct: detailTableStruct,
      listLoading: true,
      detailData: {} as ReceiveAcceptanceHistoryModel | null,
      showRefuseSignBtn: false,
      showSignatureBtn: false,
      showFooterRejectAndProcess: false,
      showRefuseSignAndSignature: false,
      getStatus(index: number) {
        if (statusOptions[index]) {
          return statusOptions[index].label
        }
      }
    })
    const initData = async() => {
      dataMap.listLoading = true
      dataMap.detailData = { ...props.approvalData } as ReceiveAcceptanceHistoryModel
      // 支付单工作表状态，0：待确认，1: 审批中, 2: 待操作, 3: 已操作, 98：签收，99：拒绝
      if (dataMap.detailData.status === 0) {
        dataMap.showFooterRejectAndProcess = true
        dataMap.showRefuseSignAndSignature = false
        dataMap.showRefuseSignBtn = false
        dataMap.showSignatureBtn = false
      } else if (dataMap.detailData.status === 2) {
        dataMap.showFooterRejectAndProcess = false
        dataMap.showRefuseSignAndSignature = true
        // 承兑接收类型，0：待确认，1: 签收, 2: 拒绝
        if (dataMap.detailData.receiveType === 1) {
          dataMap.showRefuseSignBtn = true
          dataMap.showSignatureBtn = false
        } else if (dataMap.detailData.receiveType === 2) {
          dataMap.showRefuseSignBtn = false
          dataMap.showSignatureBtn = true
        } else {
          dataMap.showRefuseSignBtn = false
          dataMap.showSignatureBtn = false
        }
      } else {
        dataMap.showFooterRejectAndProcess = false
        dataMap.showRefuseSignAndSignature = false
        dataMap.showRefuseSignBtn = false
        dataMap.showSignatureBtn = false
      }

      const params: AcceptanceBillTransfersParamModel = {
        companyCode: dataMap.detailData.companyCode,
        companyBankId: dataMap.detailData.companyBank,
        status: dataMap.detailData.status,
        draftNo: dataMap.detailData.draftNo,
        draftRange: dataMap.detailData.draftRange,
        draftType: dataMap.detailData.draftType
      }
      const data = await getAcceptanceBillTransfers(params)
      if (data && data.data) {
        dataMap.tableData = data.data
      } else {
        ElMessage({
          message: data?.msg,
          type: 'error',
          duration: 2000
        })
      }

      // Just to simulate the time of the request
      setTimeout(() => {
        dataMap.listLoading = false
      }, 0.5 * 1000)
    }
    const convertProperties = (approval: ReceiveAcceptanceHistoryModel): DraftModel => {
      const draftModel = {
        companyCode: approval.companyCode,
        companyBank: approval.companyBank,
        receiveBankName: approval.receiveBankName,
        msgseq: approval.msgseq ? approval.msgseq : null,
        draftNo: approval.draftNo,
        draftRange: approval.draftRange,
        draftAmt: approval.draftAmt,
        stdAmt: approval.stdAmt,
        splitFlag: approval.splitFlag,
        draftType: approval.draftType,
        issueDate: '',
        dueDate: '',
        rdyRspBizNo: approval.rdyRspBizNo,
        rdyRspBizType: approval.rdyRspBizType,
        requestObject: approval.requestObject,
        requestDate: '',
        banendFlag: approval.banendFlag,
        requestEntrustPay: approval.requestEntrustPay,
        bankName: approval.bankName,
        useAmt: approval.useAmt
      }
      draftModel.issueDate = dayjs(approval.issueDate).format('YYYY-MM-DD HH:mm:ss')
      draftModel.dueDate = dayjs(approval.dueDate).format('YYYY-MM-DD HH:mm:ss')
      draftModel.requestDate = approval.requestDate ? dayjs(approval.requestDate).format('YYYY-MM-DD HH:mm:ss') : ''
      return draftModel
    }
    const rejectApprovalClick = async() => {
      if (dataMap.detailData) {
        const approvalData = props.approvalData as ReceiveAcceptanceHistoryModel
        const draftModel = convertProperties(approvalData)
        const billList: DraftModel[] = [draftModel]
        const params: InitiateApprovalParamModel = {
          companyCode: dataMap.detailData.companyCode,
          bankName: dataMap.detailData.receiveBankName,
          receiveType: 2,
          totalDraftAmt: dataMap.detailData.draftAmt,
          billList: billList
        }
        const res = await initiateApproval(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          context.emit('refresh')
          dialogVisible.value = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }
    const approvalProcessClick = async() => {
      if (dataMap.detailData) {
        const approvalData = props.approvalData as ReceiveAcceptanceHistoryModel
        const draftModel = convertProperties(approvalData)
        const billList: DraftModel[] = [draftModel]
        const params: InitiateApprovalParamModel = {
          companyCode: dataMap.detailData.companyCode,
          bankName: dataMap.detailData.receiveBankName,
          receiveType: 1,
          totalDraftAmt: dataMap.detailData.draftAmt,
          billList: billList
        }
        const res = await initiateApproval(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
          context.emit('refresh')
          dialogVisible.value = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }
    const refuseSignClickClick = async() => {
      if (dataMap.detailData && dataMap.detailData.status === 2) {
        const params: ProcessingAcceptedBillsParamModel = {
          id: dataMap.detailData.id,
          msgseq: dataMap.detailData.msgseq ? dataMap.detailData.msgseq : null,
          companyCode: dataMap.detailData.companyCode,
          bankName: dataMap.detailData.receiveBankName
        }
        const res = await refuseSignForAcceptanceBill(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          context.emit('refresh')
          dialogVisible.value = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }
    const signatureClick = async() => {
      if (dataMap.detailData && dataMap.detailData.status === 2) {
        const params: ProcessingAcceptedBillsParamModel = {
          id: dataMap.detailData.id,
          msgseq: dataMap.detailData.msgseq ? dataMap.detailData.msgseq : null,
          companyCode: dataMap.detailData.companyCode,
          bankName: dataMap.detailData.receiveBankName
        }
        const res = await signForAcceptanceBill(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.operateSuccess'),
            type: 'success',
            duration: 2000
          })
          context.emit('refresh')
          dialogVisible.value = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    }
    onMounted(() => {
      initData()
    })
    return {
      t,
      tableRef,
      dialogVisible,
      rejectApprovalClick,
      approvalProcessClick,
      refuseSignClickClick,
      signatureClick,
      formatBanendFlag,
      formatSplitFlag,
      formatRequestEntrustPay,
      formatDraftType,
      ...toRefs(dataMap)
    }
  }
})
